<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Twilio from './Twilio.vue';
import ThreeSixtyDialogWhatsapp from './360DialogWhatsapp.vue';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import WahaV2Index from './whatsapp/waha_v2/Index.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAccount } from 'dashboard/composables/useAccount';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useStore();
const { isCloudFeatureEnabled } = useAccount();

const PROVIDER_TYPES = {
  WHATSAPP: 'whatsapp',
  TWILIO: 'twilio',
  WHATSAPP_CLOUD: 'whatsapp_cloud',
  WHATSAPP_EMBEDDED: 'whatsapp_embedded',
  THREE_SIXTY_DIALOG: '360dialog',
  WAHA: 'waha',
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatwootConfig?.whatsappAppId &&
    window.chatwootConfig.whatsappAppId !== 'none'
  );
});

const isWhatsappEmbeddedSignupEnabled = computed(() => {
  const accountId = route.params.accountId;
  return store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    FEATURE_FLAGS.WHATSAPP_EMBEDDED_SIGNUP
  );
});

const isWahaV2Enabled = computed(() => {
  const accountId = route.params.accountId;
  return store.getters['accounts/isFeatureEnabledonAccount'](
    accountId,
    FEATURE_FLAGS.WAHA_V2
  );
});

const selectedProvider = computed(() => route.query.provider);

const showProviderSelection = computed(() => !selectedProvider.value);

const showConfiguration = computed(() => Boolean(selectedProvider.value));

const availableProviders = computed(() => {
  const allProviders = [
    {
      key: PROVIDER_TYPES.WHATSAPP,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD_DESC'),
      icon: 'i-woot-whatsapp',
    },
    {
      key: PROVIDER_TYPES.TWILIO,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.TWILIO_DESC'),
      icon: 'i-woot-twilio',
    },
    {
      key: PROVIDER_TYPES.WAHA,
      title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WAHA'),
      description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WAHA_DESC'),
      icon: 'i-lucide-qr-code',
    },
  ];

  // Filter out WAHA provider if both WAHA and WAHA_V2 features are disabled
  return allProviders.filter(provider => {
    if (provider.key === PROVIDER_TYPES.WAHA) {
      // Show WAHA if either v1 (WAHA) or v2 (WAHA_V2) is enabled
      return isCloudFeatureEnabled(FEATURE_FLAGS.WAHA) || isWahaV2Enabled.value;
    }
    return true;
  });
});

const selectProvider = providerValue => {
  // Handle WAHA provider specially
  if (providerValue === PROVIDER_TYPES.WAHA) {
    // If WAHA v2 is enabled, show the session management page
    if (isWahaV2Enabled.value) {
      router.push({
        name: route.name,
        params: route.params,
        query: { provider: providerValue },
      });
      return;
    }

    // Otherwise, redirect to external WAHA v1 service (10210-api)
    if (typeof window === 'undefined') return;
    const whatsappApiUrl = window.chatwootConfig?.whatsappApiUrl;
    if (!whatsappApiUrl) {
      alert(
        'WhatsApp API URL not configured. Please contact your administrator.'
      );
      return;
    }
    // Use external_user_id for WAHA authentication
    const currentUser = store.getters.getCurrentUser;
    const userId =
      currentUser?.whatsapp_user_id || currentUser?.external_user_id;
    const url = userId
      ? `${whatsappApiUrl}/?user_id=${userId}`
      : `${whatsappApiUrl}/`;
    window.location.href = url;
    return;
  }

  // For other providers, navigate to configuration page
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

const shouldShowEmbeddedSignup = provider => {
  // Check if the feature is enabled for the account
  if (!isWhatsappEmbeddedSignupEnabled.value) {
    return false;
  }

  return (
    (provider === PROVIDER_TYPES.WHATSAPP && hasWhatsappAppId.value) ||
    provider === PROVIDER_TYPES.WHATSAPP_EMBEDDED
  );
};

const shouldShowCloudWhatsapp = provider => {
  // If embedded signup feature is enabled and app ID is configured, don't show cloud whatsapp
  if (isWhatsappEmbeddedSignupEnabled.value && hasWhatsappAppId.value) {
    return false;
  }

  // Show cloud whatsapp when:
  // 1. Provider is whatsapp AND
  // 2. Either no app ID is configured OR embedded signup feature is disabled
  return provider === PROVIDER_TYPES.WHATSAPP;
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div v-if="showProviderSelection">
      <div class="mb-10 text-left">
        <h1 class="mb-2 text-lg font-medium text-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-slate-11">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>

      <div class="flex gap-6 justify-start">
        <ChannelSelector
          v-for="provider in availableProviders"
          :key="provider.key"
          :title="provider.title"
          :description="provider.description"
          :icon="provider.icon"
          @click="selectProvider(provider.key)"
        />
      </div>
    </div>

    <div v-else-if="showConfiguration">
      <WahaV2Index v-if="selectedProvider === PROVIDER_TYPES.WAHA" />
      <WhatsappEmbeddedSignup
        v-else-if="shouldShowEmbeddedSignup(selectedProvider)"
      />
      <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />
      <Twilio
        v-else-if="selectedProvider === PROVIDER_TYPES.TWILIO"
        type="whatsapp"
      />
      <ThreeSixtyDialogWhatsapp
        v-else-if="selectedProvider === PROVIDER_TYPES.THREE_SIXTY_DIALOG"
      />
      <CloudWhatsapp v-else />
    </div>
  </div>
</template>
